import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPublicAppSettings } from "../../api/apiServices";

// ── Static fallback — used while API loads or if it fails ─────
const FALLBACK_SLIDES = [
  {
    _id:      "fallback-0",
    title:    "Pay Bills\nInstantly",
    subtitle: "Buy airtime, data, pay electricity, DSTV, GOtv and more — in seconds.",
    pills:    ["Airtime", "Data", "Electricity", "TV"],
    visual:   "bills",
  },
  {
    _id:      "fallback-1",
    title:    "Top Up\nAny Network",
    subtitle: "Recharge MTN, Airtel, Glo and 9mobile airtime or data — anytime, anywhere.",
    pills:    ["MTN", "Airtel", "Glo", "9mobile"],
    visual:   "airtime",
  },
  {
    _id:      "fallback-2",
    title:    "Keep the\nLights On",
    subtitle: "Buy prepaid electricity tokens for all DISCOs across Nigeria in under a minute.",
    pills:    ["EKEDC", "IKEDC", "AEDC", "PHED"],
    visual:   "electricity",
  },
  {
    _id:      "fallback-3",
    title:    "Never Miss\nYour Shows",
    subtitle: "Renew DStv, GOtv and Startimes subscriptions instantly — no queues, no agents.",
    pills:    ["DStv", "GOtv", "Startimes"],
    visual:   "cabletv",
  },
  {
    _id:      "fallback-4",
    title:    "Withdraw to\nYour Bank",
    subtitle: "Get your Naira straight to any Nigerian bank account — fast and fee-free.",
    pills:    ["GTBank", "Access", "Zenith", "UBA"],
    visual:   "bank",
  },
];

// ── Visuals ───────────────────────────────────────────────────

function BillsVisual() {
  const services = [
    { icon: "ri-phone-line",         label: "Airtime",     accent: "#059669" },
    { icon: "ri-wifi-line",          label: "Data",        accent: "#0284c7" },
    { icon: "ri-flashlight-line",    label: "Electricity", accent: "#d97706" },
    { icon: "ri-tv-2-line",          label: "Cable TV",    accent: "#7c3aed" },
    { icon: "ri-water-flash-line",   label: "Water",       accent: "#0891b2" },
    { icon: "ri-router-line",        label: "Internet",    accent: "#0f766e" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-4">
      <div className="w-full rounded-2xl px-4 py-3.5 flex items-center justify-between shadow-md"
        style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 8px 24px rgba(5,150,105,0.3)" }}>
        <div>
          <p className="text-emerald-100 text-[10px] font-medium">Easwallet Balance</p>
          <p className="text-white font-bold text-xl" style={{ fontFamily: "'Syne', sans-serif" }}>₦12,450.00</p>
        </div>
        <div className="w-9 h-9 rounded-xl bg-white/20 border border-white/30 flex items-center justify-center">
          <i className="ri-wallet-3-line text-white text-base" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 w-full">
        {services.map((s, i) => (
          <div key={s.label}
            className="flex flex-col items-center gap-1.5 py-3 rounded-2xl bg-white border border-slate-100 shadow-sm"
            style={{ animation: `floatY ${3.5 + i * 0.25}s ease-in-out infinite alternate`, animationDelay: `${i * 0.18}s` }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: s.accent + "15" }}>
              <i className={`${s.icon} text-base`} style={{ color: s.accent }} />
            </div>
            <span className="text-slate-500 text-[9px] font-semibold">{s.label}</span>
          </div>
        ))}
      </div>
      <div className="w-full py-2.5 rounded-xl text-center text-xs font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
        <i className="ri-flashlight-fill mr-1" />Pay any bill in under 10 seconds
      </div>
    </div>
  );
}

function AirtimeVisual() {
  const networks = [
    { name: "MTN",     color: "#f5c000", text: "#7a5c00", short: "M" },
    { name: "Airtel",  color: "#e8001c", text: "#fff",    short: "A" },
    { name: "Glo",     color: "#008000", text: "#fff",    short: "G" },
    { name: "9mobile", color: "#006633", text: "#fff",    short: "9" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-4">
      <div className="w-full rounded-2xl px-5 py-4 shadow-md text-center"
        style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 10px 30px rgba(5,150,105,0.35)" }}>
        <p className="text-emerald-100 text-[10px] font-medium mb-1">Recharging</p>
        <p className="text-white text-3xl font-bold" style={{ fontFamily: "'Syne', sans-serif" }}>₦1,000</p>
        <div className="mt-2 flex items-center justify-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-300 animate-pulse" />
          <span className="text-emerald-200 text-xs">Processing...</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2.5 w-full">
        {networks.map((n, i) => (
          <div key={n.name}
            className="flex items-center gap-2.5 rounded-xl px-3 py-3 bg-white shadow-sm border border-slate-100"
            style={{ animation: `floatY ${3.5 + i * 0.3}s ease-in-out infinite alternate`, animationDelay: `${i * 0.2}s` }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center font-black text-sm flex-shrink-0"
              style={{ background: n.color, color: n.text }}>
              {n.short}
            </div>
            <div>
              <p className="text-slate-700 text-xs font-semibold">{n.name}</p>
              <p className="text-slate-400 text-[9px]">Airtime & Data</p>
            </div>
          </div>
        ))}
      </div>
      <div className="w-full py-2.5 rounded-xl text-center text-xs font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
        <i className="ri-phone-fill mr-1" />All networks. One tap.
      </div>
    </div>
  );
}

function ElectricityVisual() {
  const discos = [
    { short: "EKE", name: "EKEDC", color: "#059669" },
    { short: "IKE", name: "IKEDC", color: "#0284c7" },
    { short: "AED", name: "AEDC",  color: "#7c3aed" },
    { short: "PHE", name: "PHED",  color: "#d97706" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-4">
      {/* Token card */}
      <div className="w-full rounded-2xl px-5 py-4 shadow-md"
        style={{ background: "linear-gradient(135deg, #d97706, #f59e0b)", boxShadow: "0 10px 30px rgba(217,119,6,0.35)" }}>
        <div className="flex items-center justify-between mb-2">
          <p className="text-amber-100 text-[10px] font-medium">Prepaid Token</p>
          <i className="ri-flashlight-fill text-amber-200 text-sm" />
        </div>
        <p className="text-white text-xl font-bold tracking-widest" style={{ fontFamily: "'Syne', sans-serif", letterSpacing: "0.12em" }}>
          4521 • 8834 • 1190
        </p>
        <div className="flex items-center justify-between mt-2">
          <span className="text-amber-200 text-[10px]">100 kWh · ₦5,000</span>
          <span className="text-amber-100 text-[10px] font-semibold bg-white/20 px-2 py-0.5 rounded-full">Active</span>
        </div>
      </div>
      {/* DISCOs */}
      <div className="grid grid-cols-2 gap-2 w-full">
        {discos.map((d, i) => (
          <div key={d.name}
            className="flex items-center gap-2 rounded-xl px-3 py-2.5 bg-white border border-slate-100 shadow-sm"
            style={{ animation: `floatY ${3.5 + i * 0.25}s ease-in-out infinite alternate`, animationDelay: `${i * 0.2}s` }}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
              style={{ background: d.color }}>
              {d.short}
            </div>
            <span className="text-slate-600 text-xs font-medium">{d.name}</span>
          </div>
        ))}
      </div>
      <div className="w-full py-2.5 rounded-xl text-center text-xs font-bold bg-amber-50 border border-amber-200 text-amber-700">
        <i className="ri-flashlight-fill mr-1" />All 11 DISCOs supported
      </div>
    </div>
  );
}

function CableTVVisual() {
  const providers = [
    { name: "DStv",      color: "#0066b2", text: "#fff", icon: "ri-tv-2-fill",       desc: "Premium · Compact · Access" },
    { name: "GOtv",      color: "#e8001c", text: "#fff", icon: "ri-live-line",        desc: "Max · Jolli · Lite" },
    { name: "Startimes", color: "#f5c000", text: "#7a5c00", icon: "ri-broadcast-line", desc: "Nova · Basic · Smart" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-3 px-4">
      <div className="w-full rounded-2xl px-4 py-3 shadow-md flex items-center gap-3"
        style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 8px 24px rgba(5,150,105,0.3)" }}>
        <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
          <i className="ri-tv-2-fill text-white text-lg" />
        </div>
        <div>
          <p className="text-emerald-100 text-[10px] font-medium">Renewing subscription</p>
          <p className="text-white font-bold text-base" style={{ fontFamily: "'Syne', sans-serif" }}>DStv Premium</p>
        </div>
        <div className="ml-auto">
          <span className="text-emerald-100 text-[10px] font-semibold bg-white/20 px-2 py-1 rounded-full">₦24,500</span>
        </div>
      </div>
      <div className="flex flex-col gap-2 w-full">
        {providers.map((p, i) => (
          <div key={p.name}
            className="flex items-center gap-3 rounded-xl px-3 py-2.5 bg-white border border-slate-100 shadow-sm"
            style={{ animation: `floatY ${3.5 + i * 0.35}s ease-in-out infinite alternate`, animationDelay: `${i * 0.25}s` }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: p.color }}>
              <i className={`${p.icon} text-base`} style={{ color: p.text }} />
            </div>
            <div className="flex-1">
              <p className="text-slate-700 text-xs font-semibold">{p.name}</p>
              <p className="text-slate-400 text-[9px]">{p.desc}</p>
            </div>
            <i className="ri-arrow-right-s-line text-slate-300 text-base" />
          </div>
        ))}
      </div>
      <div className="w-full py-2.5 rounded-xl text-center text-xs font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
        <i className="ri-tv-2-fill mr-1" />Renew in under 30 seconds
      </div>
    </div>
  );
}

function BankVisual() {
  const banks = [
    { name: "GTBank", color: "#e85d04", short: "GT" },
    { name: "Access",  color: "#0066b2", short: "AC" },
    { name: "Zenith",  color: "#cc0000", short: "ZN" },
    { name: "UBA",     color: "#cc0000", short: "UB" },
  ];
  return (
    <div className="relative w-full h-full flex flex-col items-center justify-center gap-4 px-4">
      <div className="w-full rounded-2xl px-5 py-4 shadow-md"
        style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 10px 30px rgba(5,150,105,0.35)" }}>
        <p className="text-emerald-100 text-xs mb-1">Available Balance</p>
        <p className="text-white text-3xl font-bold" style={{ fontFamily: "'Syne', sans-serif" }}>₦245,800</p>
        <p className="text-emerald-200 text-xs mt-1">Ready to withdraw</p>
      </div>
      <div className="grid grid-cols-2 gap-2.5 w-full">
        {banks.map(b => (
          <div key={b.name} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 bg-white shadow-sm border border-slate-100">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0"
              style={{ background: b.color }}>{b.short}</div>
            <span className="text-slate-600 text-xs font-medium">{b.name}</span>
          </div>
        ))}
      </div>
      <div className="w-full py-3 rounded-xl text-center text-sm font-bold bg-emerald-50 border border-emerald-200 text-emerald-700">
        <i className="ri-send-plane-fill mr-1.5" />Transfer to any bank instantly
      </div>
    </div>
  );
}

const VISUAL_MAP = {
  bills:       BillsVisual,
  airtime:     AirtimeVisual,
  electricity: ElectricityVisual,
  cabletv:     CableTVVisual,
  bank:        BankVisual,
};
// ─────────────────────────────────────────────────────────────
export default function Onboarding() {
  const navigate = useNavigate();

  // ── Fetch slides from API, fall back to static on error ───
  const [slides, setSlides] = useState(FALLBACK_SLIDES);
  const [slidesLoaded, setSlidesLoaded] = useState(false);
  const [brandLoading, setBrandLoading] = useState(false);
  const [branding, setBranding] = useState("");
  const [searchParams] = useSearchParams();
  const next = searchParams.get("next") || "/home";

  useEffect(() => {
    const BASE = import.meta.env.VITE_API_URL || "";
    fetch(`${BASE}/api/mtrade/onboarding/slides`)
      .then(r => r.json())
      .then(res => {
        if (res.status && Array.isArray(res.data) && res.data.length > 0) {
          setSlides(res.data);
        }
        // If API returns empty array, keep fallback slides
      })
      .catch(() => { /* silent — keep fallback */ })
      .finally(() => setSlidesLoaded(true));
  }, []);

  useEffect(() => {
      getPublicAppSettings()
        .then(res => {
          if (!res.status) return;
          const { branding: rawBranding = {}, } = res.data;

          setBranding(rawBranding);
        })
        .catch(console.error)
        .finally(() => setBrandLoading(false));
    }, []);

  // ── Slide state ───────────────────────────────────────────
  const [current,      setCurrent]      = useState(0);
  const [dragging,     setDragging]     = useState(false);
  const [dragOffset,   setDragOffset]   = useState(0);
  const [transitioning, setTransitioning] = useState(false);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const vibrate = (ms = 15) => navigator.vibrate?.(ms);

  const goTo = (index) => {
    if (index < 0 || index >= slides.length || transitioning) return;
    setTransitioning(true);
    setCurrent(index);
    setDragOffset(0);
    vibrate();
    setTimeout(() => setTransitioning(false), 350);
  };

  const goToNext = () => {
    localStorage.setItem("hasSeenOnboarding", "true");
    navigate(decodeURIComponent(next), { replace: true });
  };

  const handleNext = () => {
  if (current < slides.length - 1) {
    goTo(current + 1);
  } else {
    vibrate(30);
    goToNext();          // ← uses next param
  }
};

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; touchStartY.current = e.touches[0].clientY; setDragging(true); };
  const onTouchMove  = (e) => {
    if (!touchStartX.current) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dy) > Math.abs(dx)) return;
    e.preventDefault();
    setDragOffset(dx);
  };
  const onTouchEnd = () => {
    setDragging(false);
    if (dragOffset < -60 && current < slides.length - 1) goTo(current + 1);
    else if (dragOffset > 60 && current > 0) goTo(current - 1);
    else setDragOffset(0);
    touchStartX.current = null;
  };

  const onMouseDown = (e) => { touchStartX.current = e.clientX; setDragging(true); };
  const onMouseMove = (e) => { if (dragging && touchStartX.current) setDragOffset(e.clientX - touchStartX.current); };
  const onMouseUp   = () => {
    if (dragOffset < -60 && current < slides.length - 1) goTo(current + 1);
    else if (dragOffset > 60 && current > 0) goTo(current - 1);
    else setDragOffset(0);
    setDragging(false);
    touchStartX.current = null;
  };

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowRight") goTo(current + 1);
      if (e.key === "ArrowLeft")  goTo(current - 1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [current, slides.length]);

  const slide  = slides[current];
  const Visual = VISUAL_MAP[slide?.visual] || CryptoVisual;

  return (
    <div
      className="min-h-screen bg-white flex flex-col select-none overflow-hidden"
      style={{ cursor: dragging ? "grabbing" : "grab" }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}   onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}
    >
      <div className="absolute top-0 left-0 right-0 h-[55%] pointer-events-none"
        style={{ background: "linear-gradient(180deg, #ecfdf5 0%, #f0fdf4 60%, transparent 100%)" }} />
      <div className="absolute inset-0 pointer-events-none opacity-30"
        style={{ backgroundImage: "radial-gradient(circle, #86efac 1px, transparent 1px)", backgroundSize: "28px 28px",
          maskImage: "linear-gradient(180deg, rgba(0,0,0,0.4) 0%, transparent 60%)" }} />

      {/* Header */}
      <div className="relative z-10 px-6 pt-12 pb-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-md"
            >
          <img src={branding.logoUrl} alt="" />
          </div>
          <span className="text-slate-800 font-bold text-lg tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            easwallet
          </span>
        </div>
        <button
          onClick={goToNext}
          className="text-slate-400 text-sm font-medium hover:text-slate-600 transition-colors px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200"
        >
          Skip
        </button>
      </div>

      {/* Slide */}
      <div className="relative z-10 flex-1 flex flex-col px-6 pt-2 pb-6"
        style={{
          transform: dragging ? `translateX(${dragOffset * 0.25}px)` : "translateX(0)",
          transition: dragging ? "none" : "transform 0.35s cubic-bezier(0.25,0.46,0.45,0.94)",
        }}>

        {/* Visual card */}
        <div className="flex-1 flex items-center justify-center py-4 min-h-0">
          <div className="w-full max-w-xs aspect-square rounded-3xl overflow-hidden relative shadow-xl"
            style={{ background: "linear-gradient(145deg, #f0fdf4, #ecfdf5)", border: "1.5px solid #bbf7d0",
              boxShadow: "0 20px 60px rgba(5,150,105,0.15), 0 4px 16px rgba(0,0,0,0.06)" }}>
            <div className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at 30% 20%, rgba(110,231,183,0.3), transparent 60%)" }} />
            <Visual />
          </div>
        </div>

        {/* Counter */}
        <div className="flex justify-center mb-3">
          <span className="text-slate-300 text-xs font-mono tracking-widest">
            {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
          </span>
        </div>

        {/* Text */}
        <div className="mb-6">
          <h1 className="text-slate-800 font-black leading-tight mb-3 whitespace-pre-line"
            style={{ fontFamily: "'Syne', sans-serif", fontSize: "clamp(1.8rem, 8.5vw, 2.6rem)", letterSpacing: "-0.03em" }}>
            {slide?.title}
          </h1>
          <p className="text-slate-500 text-sm leading-relaxed">{slide?.subtitle}</p>

          {/* Pills — from the slide data */}
          {slide?.pills?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {slide.pills.map(pill => (
                <span key={pill}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {pill}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mb-5">
          {slides.map((_, i) => (
            <button key={i} onClick={() => goTo(i)} className="rounded-full transition-all duration-300"
              style={{ width: i === current ? "28px" : "8px", height: "8px",
                background: i === current ? "#059669" : "#bbf7d0",
                boxShadow: i === current ? "0 0 10px rgba(5,150,105,0.5)" : "none" }} />
          ))}
        </div>

        {/* CTA */}
        <button onClick={handleNext}
          className="w-full py-4 rounded-2xl font-bold text-base transition-all duration-200 active:scale-[0.98] flex items-center justify-center gap-2 text-white"
          style={{ background: "linear-gradient(135deg, #059669, #10b981)", boxShadow: "0 6px 24px rgba(5,150,105,0.45)", fontFamily: "'Syne', sans-serif" }}>
          {current === slides.length - 1 ? "Get Started" : "Continue"}
          <i className="ri-arrow-right-line" />
        </button>

        <p className="text-center text-slate-400 text-sm mt-4">
          Already have an account?{" "}
          <button onClick={goToNext}
            className="text-emerald-600 font-bold hover:text-emerald-700 transition-colors">
            Sign In
          </button>
        </p>
      </div>

      {current === 0 && (
        <div className="absolute bottom-36 right-5 z-20 pointer-events-none">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 border border-slate-200">
            <i className="ri-hand-line text-slate-400 text-sm animate-pulse" />
            <span className="text-slate-400 text-[10px] font-medium">Swipe to explore</span>
          </div>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&display=swap');
        @keyframes floatY {
          from { transform: translateY(0px); }
          to   { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
}
